import type {
  ChangeEmergencyPolicy,
  ChangeEmergencyPolicyManagerArea,
  ChangeTypeApprovalFlow,
  ChangeWorkGroup,
  ChangeWorkGroupMember,
} from '../../types/changes';

export function membersForGroup(areas: ChangeWorkGroup[], groupId: number): ChangeWorkGroupMember[] {
  if (!groupId) return [];
  const area = areas.find((item) => item.group_id === groupId);
  return area?.members || [];
}

export function memberLabel(member: ChangeWorkGroupMember): string {
  const name = (member.name || '').trim() || `Usuário #${member.user_id}`;
  const email = (member.email || '').trim();
  return email ? `${name} (${email})` : name;
}

/** Resumo para a UI: passos ativos do fluxo sem gestor. */
export function countFlowStepsMissingManagers(
  flow: ChangeTypeApprovalFlow,
  policy: ChangeEmergencyPolicy,
  workGroups: ChangeWorkGroup[],
): number {
  const rows = mergeManagerAreasForDisplay(policy, workGroups, flow);
  const stepIds = new Set(
    flow.steps.filter((item) => item.is_active).map((item) => item.group_id),
  );
  return rows.filter((row) => stepIds.has(row.group_id) && !row.has_manager).length;
}

export function workGroupOptions(areas: ChangeWorkGroup[]) {
  return areas.map((item) => ({
    value: item.group_id,
    label: item.name || `Área #${item.group_id}`,
  }));
}

export function mergeManagerAreasForDisplay(
  policy: ChangeEmergencyPolicy,
  workGroups: ChangeWorkGroup[],
  flow?: ChangeTypeApprovalFlow,
): ChangeEmergencyPolicyManagerArea[] {
  const byId = new Map<number, ChangeEmergencyPolicyManagerArea>();
  const ensure = (groupId: number, name?: string | null) => {
    if (!byId.has(groupId)) {
      const area = workGroups.find((item) => item.group_id === groupId);
      const fromGroup = (area?.members || [])
        .filter((member) => member.is_manager)
        .map((member) => ({
          user_id: member.user_id,
          user_name: member.name ?? null,
          user_email: member.email ?? null,
        }));
      const fixed = policy.managers
        .filter((item) => item.group_id === groupId)
        .map((item) => ({
          user_id: item.user_id,
          user_name: item.user_name ?? null,
          user_email: item.user_email ?? null,
        }));
      byId.set(groupId, {
        group_id: groupId,
        name: name ?? area?.name ?? null,
        has_manager: fromGroup.length > 0 || fixed.length > 0,
        from_group: fromGroup,
        fixed_managers: fixed,
      });
    }
  };
  flow?.steps.forEach((item) => {
    if (item.is_active) ensure(item.group_id, item.name);
  });
  policy.fixed_areas.forEach((item) => {
    if (item.is_active) ensure(item.group_id, item.name);
  });
  policy.managers.forEach((item) => ensure(item.group_id, item.group_name));
  return Array.from(byId.values()).sort((a, b) => a.group_id - b.group_id);
}
